import { GeneratedContent } from "@/types/content";
import { formatDistanceToNow } from "date-fns";
import { MessageCircle, Repeat2, Heart, Share, MoreHorizontal, Verified } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { copyFormattedContent } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";

interface TwitterPreviewProps {
  content: GeneratedContent;
}

export const TwitterPreview = ({ content }: TwitterPreviewProps) => {
  const { toast } = useToast();
  const [copiedTweetIndex, setCopiedTweetIndex] = useState<number | null>(null);
  
  // Add null safety for content
  const tweets = content?.content?.split('\n---\n') || [''];
  
  // Fetch project data to get business name
  const { data: projectData } = useQuery({
    queryKey: ["project", content?.project_id],
    queryFn: async () => {
      if (!content?.project_id) return null;
      
      const { data, error } = await supabase
        .from("projects")
        .select("business_name")
        .eq("id", content.project_id)
        .single();

      if (error) {
        console.error("Error fetching project:", error);
        return null;
      }
      
      return data;
    },
    enabled: !!content?.project_id,
  });

  const businessName = projectData?.business_name || "Your Business";
  const handle = businessName.toLowerCase().replace(/\s+/g, '');

  // Format text to handle bold and headers
  const formatTweetText = (text: string) => {
    if (!text) return text;
    
    // Process the content in stages
    let formattedText = text;
    
    // Convert headers (# and ## and ###) to bold with different sizes
    const headerRegex = /^(#{1,3})\s+(.+)$/gm;
    formattedText = formattedText.replace(headerRegex, (match, hashes, title) => {
      const level = hashes.length;
      let className = 'text-base font-semibold'; // Default for ###
      
      if (level === 1) {
        className = 'text-lg font-bold'; // For #
      } else if (level === 2) {
        className = 'text-base font-bold'; // For ##
      }
      
      return `<div class="${className} my-1">${title}</div>`;
    });
    
    // Convert **bold** text
    const boldRegex = /\*\*([^*]+)\*\*/g;
    formattedText = formattedText.replace(boldRegex, '<strong>$1</strong>');
    
    return formattedText;
  };

  const handleCopyTweet = (tweetText: string, index: number) => {
    if (!tweetText || tweetText.trim() === '') {
      toast({
        title: "Cannot copy",
        description: "There is no content to copy.",
        variant: "destructive"
      });
      return;
    }
    
    // The copyFormattedContent utility now handles cleaning and formatting.
    // We just need to pass the raw tweet text.
    copyFormattedContent(tweetText, {
      onSuccess: () => {
        setCopiedTweetIndex(index);
        toast({
          title: "Copied to clipboard",
          description: "Tweet has been copied with formatting.", // Formatting is attempted
        });
        setTimeout(() => setCopiedTweetIndex(null), 2000);
      },
      onError: (error) => {
        // Log the specific error from the utility for debugging
        console.error("Copy operation failed:", error.message);
        
        // Provide a user-friendly message. 
        // The utility function logs detailed reasons for the failure.
        toast({
          title: "Copy Failed",
          description: "Could not copy tweet to clipboard. Formatting might not be supported.",
          variant: "destructive",
        });
      }
    });
  };

  return (
    <div className="max-w-[550px] mx-auto space-y-3">
      {tweets.map((tweet, index) => {
        const formattedTweet = formatTweetText(tweet.trim());
        const isCopied = copiedTweetIndex === index;
        
        return (
          <div 
            key={index}
            className="bg-white dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-800 shadow-sm"
          >
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-full bg-gray-200 dark:bg-gray-800 flex items-center justify-center text-gray-500 font-bold">
                {businessName.charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1">
                  <span className="font-bold truncate">{businessName}</span>
                  <Verified className="h-4 w-4 text-blue-500" />
                  <span className="text-gray-500 text-sm ml-1">@{handle}</span>
                  <span className="text-gray-500 text-sm">·</span>
                  <span className="text-gray-500 text-sm">
                    {formatDistanceToNow(new Date(), { addSuffix: true })}
                  </span>
                  <div className="ml-auto">
                    <MoreHorizontal className="h-4 w-4 text-gray-500" />
                  </div>
                </div>
                
                <div 
                  className="mt-2 text-[15px] whitespace-pre-wrap"
                  dangerouslySetInnerHTML={{ __html: formattedTweet }}
                />

                <div className="mt-4 flex items-center justify-between text-gray-500">
                  <button className="flex items-center gap-1 hover:text-blue-500 transition-colors">
                    <MessageCircle className="h-4 w-4" />
                    <span className="text-xs">{Math.floor(Math.random() * 10)}</span>
                  </button>
                  <button className="flex items-center gap-1 hover:text-green-500 transition-colors">
                    <Repeat2 className="h-4 w-4" />
                    <span className="text-xs">{Math.floor(Math.random() * 50)}</span>
                  </button>
                  <button className="flex items-center gap-1 hover:text-red-500 transition-colors">
                    <Heart className="h-4 w-4" />
                    <span className="text-xs">{Math.floor(Math.random() * 100)}</span>
                  </button>
                  <button 
                    className={`flex items-center gap-1 ${isCopied ? 'text-blue-500' : 'hover:text-blue-500'} transition-colors`}
                    onClick={() => handleCopyTweet(tweet, index)}
                  >
                    <Share className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
            
            {index < tweets.length - 1 && (
              <div className="ml-6 mt-2 mb-2 w-0.5 h-6 bg-gray-300 dark:bg-gray-700" />
            )}
          </div>
        );
      })}
    </div>
  );
};
